import NavBar from "../../components/navbar/NavBar";
import {codeCatalogNavItem} from "../../components/appbar/menu/const";
import Typography from "@mui/material/Typography";
import {Box, Divider, Collapse} from "@mui/material";
import SearchBar from "../downloads/SearchBar";
import useTranslation from "../../hooks/usetranslation";
import React, {useEffect, useMemo, useState} from "react";
import Grid from "@mui/material/Grid";
import {useCategoriesTreeItemQuery, useCodeCatalogQuery} from "../../graphql";
import Spinner from "../../components/Spinner";
import GqlError from "../../components/error/GqlError";
import FormSelectCodeCatalog from "./FormSelectCodeCatalog";
import CodeCatalogWrapper from "./CodeCatalogWrapper";
import {
    CloseIcon,
    FolderOpenIcon,
} from "../../components/icons/MANIcons";
import CodeCatalogHierarchy from "./CodeCatalogHierarchy";
import Stack from "@mui/material/Stack";
import CodeCatalogListViewItem from "./CodeCatalogListViewItem";
import useLocale from "../../hooks/uselocale";
import {formatToMonthYear, mappedLocales} from "../../lib/utils";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import {useSearchParams} from "react-router-dom";
import ActiveFilter from "../downloads/filters/ActiveFilter";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import AvailableDatePicker from "./AvailableDatePicker";
import dayjs from "dayjs";

const CODE_CATALOG_LIMIT_PER_PAGE = 12;


const CodeCatalogView = () => {
    const {t} = useTranslation();
    const {locale, dateLocale} = useLocale();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isHierarchyExpanded, setIsHierarchyExpanded] = useState(false);
    const baseVehicle = searchParams.get("baseVehicle") || "";
    const defaultPathname = searchParams.get("default") || "";
    const page = searchParams.get("page") ? parseInt(searchParams.get("page") as string) : 1;
    const showHierarchyDelete = searchParams.get("category") || searchParams.get("parentCategory") || searchParams.get("subCategory");


    const selectedFilters = useMemo(() => {
        return {
            series: searchParams.getAll("series"),
            types: searchParams.getAll("types"),
            genre: searchParams.getAll("genre"),
            validityTime: searchParams.get("validityTime") ? [formatToMonthYear(searchParams.get("validityTime") ?? "")] : [],
            query: searchParams.get("query") ? [searchParams.get("query") as string] : [],
            code: searchParams.get("code") ? [searchParams.get("code") as string] : [],
        };
    }, [searchParams])

    const whereInput = useMemo(() => {
        const rawCategoryID = searchParams.get("parentCategory") || "";
        const rawSubCategoryID = searchParams.get("subCategory") || "";

        // keep your existing behavior: genre falls back to category when genre is empty
        const rawGenre =
            searchParams.getAll("genre").length ? searchParams.getAll("genre") : searchParams.getAll("category");

        const hasCategory = Boolean(rawCategoryID);
        const hasSubCategory = Boolean(rawSubCategoryID);
        const hasGenre = Array.isArray(rawGenre) && rawGenre.length > 0;

        const filter = {
            series: searchParams.getAll("series"),
            type: searchParams.getAll("types"),
            genre: rawGenre,
            categoryID: rawCategoryID,
            subCategoryID: rawSubCategoryID,
            validityTime: formatToMonthYear(searchParams.get("validityTime")) ?? "",
            query: searchParams.get("query") || "",
        };

        if (hasCategory && !hasSubCategory && !hasGenre) {
            filter.subCategoryID = "";
            filter.genre = [];
        } else if (hasCategory && hasSubCategory && !hasGenre) {
            filter.categoryID = "";
            filter.genre = [];
        } else if (hasCategory && !hasSubCategory && hasGenre) {
            filter.categoryID = "";
            filter.subCategoryID = "";

        }else if (hasCategory && hasSubCategory && !hasGenre) {
            filter.categoryID = "";
            filter.genre=[];
        } else if (hasCategory && hasSubCategory && hasGenre) {
            filter.categoryID = "";
            filter.subCategoryID = "";
        }

        return {
            pagination: {
                page: parseInt(searchParams.get("page") as string) || 1,
                limit: CODE_CATALOG_LIMIT_PER_PAGE,
            },
            filter,
            locale: mappedLocales(locale),
            code: searchParams.get("code") || "",
        };
    }, [searchParams, locale]);

    const [{data, fetching, error}] = useCodeCatalogQuery({
        variables: {
            where: whereInput,
            locale: mappedLocales(locale),
        }
    });
    const [{data: hierarchyData, fetching: hierarchyFetching, error: hierarchyError}] = useCategoriesTreeItemQuery({
        variables: {
            locale: mappedLocales(locale),
        }
    });

    const handleSetSearchParam = (searchParam: string) => {
        const paramsObj = Object.fromEntries(searchParams.entries());
        delete paramsObj.page; // Remove the page param
        paramsObj.query = searchParam; // Set the new code param
        setSearchParams(new URLSearchParams(paramsObj));
    };
    const extractBaseVehicle = (baseVehicle: string) => {
        return baseVehicle.slice(1, 4);
    }
    // Add this function inside your component
    const handleClearFilter = (filterKey: string, filterValue: string) => {
        const params = new URLSearchParams(searchParams.toString());
        if (filterKey === "validityTime") {
            params.delete("validityTime");
        } else if (filterKey === "query") {
            params.delete("query");
        } else if (filterKey === "code") {
            params.delete("code");
        } else {
            const values = params.getAll(filterKey).filter(v => v !== filterValue);
            params.delete(filterKey);
            values.forEach(v => params.append(filterKey, v));
        }
        setSearchParams(params);
    };

    useEffect(() => {
        const params = new URLSearchParams(searchParams.toString());

        if (defaultPathname) {
            const currentMonth = dayjs().format("MM");
            const currentYear = dayjs().format("YYYY");
            params.delete("default");
            if (!params.has("validityTime")) {
                params.set("validityTime", `${currentYear}-${currentMonth}-01T00:00:00Z`);
            }
        }

        if (baseVehicle && !searchParams.getAll("types").includes(baseVehicle)) {
            params.append("types", extractBaseVehicle(baseVehicle));
            params.delete("baseVehicle");
        }

        if (data?.codeCatalog?.data?.pagination.totalCount) {
            if (data.codeCatalog.data.pagination.totalCount <= CODE_CATALOG_LIMIT_PER_PAGE) {
                params.delete("page");
            } else {
                const currentPage = searchParams.get("page") ?? "1";
                params.set("page", currentPage);
            }
        }

        setSearchParams(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultPathname, baseVehicle, data]);


    if (error) {
        return <GqlError error={error}/>
    }
    if (hierarchyFetching) {
        return <Spinner/>
    }


    const toggleHierarchy = () => {
        setIsHierarchyExpanded(prevState => !prevState);
    };

    const hierarchyMdSize = isHierarchyExpanded ? 4 : 0.6; // Hierarchy takes 4 when open, 1 when collapsed (for the tab)
    const contentMdSize = isHierarchyExpanded ? 8 : 11.4; // Content takes 8 when hierarchy is open, 11 when collapsed (4+8=12, 1+11=12)

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <NavBar links={[{label: t(codeCatalogNavItem.id as string)}]}/>
            <CodeCatalogWrapper
                codeCatalogHeader={
                    <>
                        <Typography variant="h2">{t("codeCatalog")}</Typography>
                        <Box sx={{display: "flex", justifyContent: "center", mt: 3, mb: 4}}>
                            <SearchBar setSearchParam={handleSetSearchParam}
                                       initialSearchText={searchParams.get("query") || ""}
                                       placeholder={t("search_code")}/>
                        </Box>
                    </>
                }
            >
                <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                        <FormSelectCodeCatalog
                            label={t("codeCatalog_series")}
                            options={data?.codeCatalog?.data?.options?.series?.map((item) => ({
                                label: item?.key ?? "",
                                value: item?.value ?? ""
                            })) || []}
                            value={searchParams.getAll("series")}
                            onChange={(newSeries) => {
                                const params = new URLSearchParams(searchParams.toString());
                                params.delete("series");
                                params.delete("category");
                                params.delete("page");
                                newSeries.forEach(s => params.append("series", s));
                                setSearchParams(params);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormSelectCodeCatalog
                            label={t("codeCatalog_type")}
                            options={data?.codeCatalog?.data?.options?.type?.map((item) => ({
                                label: item?.key ?? "",
                                value: item?.value ?? ""
                            })) || []}
                            value={searchParams.getAll("types")}
                            onChange={(newTypes) => {
                                const params = new URLSearchParams(searchParams.toString());
                                params.delete("types");
                                params.delete("category");
                                params.delete("page");
                                newTypes.forEach(s => params.append("types", s));
                                setSearchParams(params);
                            }}
                        />
                    </Grid>
                    <Grid item container xs={12} md={3} spacing={1}>
                        <AvailableDatePicker/>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormSelectCodeCatalog
                            label={t("codeCatalog_genre")}
                            options={data?.codeCatalog?.data?.options?.genre?.map((item) => ({
                                label: `${item?.value} - ${item?.key}` ?? "",
                                value: item?.value ?? ""
                            })) || []}
                            value={searchParams.getAll("genre")}
                            searchable
                            onChange={(newGenre) => {
                                const params = new URLSearchParams(searchParams.toString());
                                params.delete("genre");
                                params.delete("category");
                                params.delete("page");
                                newGenre.forEach(s => params.append("genre", s));
                                setSearchParams(params);
                            }}
                            disabled={searchParams.get("category") !== null}
                        />
                    </Grid>
                </Grid>


                <Grid item xs={12} md={12}>
                    {!!Object.values(selectedFilters).flat().length && (
                        <Stack direction="row" alignItems="center" flexWrap="wrap" marginTop={3}>
                            <Typography variant={"h5"} marginX={1}>{t("active_filters")}</Typography>
                            {Object.entries(selectedFilters).map(([filterKey, filterValues]) =>
                                filterValues.map((filterValue, idx) => {
                                    let displayName: string;
                                    switch (filterKey) {
                                        case "genre":
                                            displayName = `${filterValue} - ${data?.codeCatalog?.data?.options?.genre?.find((item) => item?.value === filterValue)?.key || filterValue}`;
                                            break;
                                        case "query":
                                            displayName = `Query: ${filterValue}`;
                                            break;
                                        case "code":
                                            displayName = `Code: ${filterValue}`;
                                            break;
                                        default:
                                            displayName = filterValue;
                                    }

                                    return (
                                        <ActiveFilter
                                            key={`${filterKey}-${filterValue}-${idx}`}
                                            name={displayName}
                                            clearFilter={() => handleClearFilter(filterKey, filterValue)}
                                            backgroundColor={"background.paper"}
                                        />
                                    );
                                })
                            )}
                            <Button color="secondary" onClick={() => setSearchParams(new URLSearchParams())}
                                    sx={{mt: 0.5}}>
                                {t("clear_all")}
                            </Button>
                        </Stack>
                    )}
                </Grid>
                <Divider sx={{my: 2}}/>
                <Grid container spacing={1} alignItems="flex-start">
                    <Grid item xs={12} sx={{width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
                        <Stack>
                            <Typography variant={"h5"} textTransform={"uppercase"}>{t('content')}</Typography>
                            <Typography variant={"caption"}
                                        sx={{fontSize: 14}}>{`${data?.codeCatalog?.data.pagination.totalCount} codes`}{
                                showHierarchyDelete && <IconButton onClick={() => {
                                    const newSearchParams = new URLSearchParams(searchParams);
                                    newSearchParams.delete('category');
                                    newSearchParams.delete('parentCategory');
                                    newSearchParams.delete('subCategory');
                                    setSearchParams(newSearchParams);
                                }}><CloseIcon sx={{width: 14}}/></IconButton>}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item container xs={12}>
                        <Grid item xs={hierarchyMdSize} md={hierarchyMdSize}>
                            {hierarchyFetching && (
                                <Spinner/>
                            )}
                            {!hierarchyFetching && <Stack direction="row" sx={{
                                width: '100%',
                                height: '100%',
                                minHeight: "fit-content !important"
                            }} alignItems="stretch">
                                <Collapse in={isHierarchyExpanded} orientation="horizontal" collapsedSize={0}
                                          sx={{
                                              flexShrink: 0,
                                              flexGrow: 0,
                                          }}
                                >
                                    <Box sx={{minWidth: 200, width: 330, height: '100%'}}>
                                        <CodeCatalogHierarchy hierarchyData={hierarchyData}
                                                              hierarchyFetching={hierarchyFetching}
                                                              hierarchyError={hierarchyError}/>
                                    </Box>
                                </Collapse>
                                <Box
                                    sx={{
                                        backgroundColor: "background.paper",
                                        display: 'flex',
                                        flexDirection: 'row-reverse',
                                        writingMode: "vertical-rl",
                                        color: "text.secondary",
                                        borderTopRightRadius: 6,
                                        borderBottomRightRadius: 6,
                                        cursor: "pointer",
                                        flexShrink: 0,
                                        minWidth: '48px', // Fixed min-width for the tab
                                        width: '48px', // Fixed width for the tab

                                    }}
                                    onClick={toggleHierarchy}
                                >
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        flex: 1,
                                        justifyContent: "flex-end",
                                        marginTop: 2,
                                        transform: "rotate(180deg)",
                                        cursor: "pointer"
                                    }}>
                                        <Typography
                                            textTransform="uppercase"
                                            padding={1}
                                            fontSize={16}
                                            fontWeight={"bolder"}
                                        >
                                            {t("code_catalog_categories")}
                                        </Typography>
                                        <FolderOpenIcon sx={{transform: "rotate(90deg)"}}/>
                                    </Box>
                                </Box>
                            </Stack>}
                        </Grid>
                        <Grid
                            item
                            container
                            xs={contentMdSize}
                            md={contentMdSize}
                            sx={{height: '100%'}}
                        >
                            {fetching && (
                                <Spinner/>
                            )}
                            {!fetching &&
                                <>
                                    {data?.codeCatalog?.data?.hits.length === 0 && (
                                        <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
                                            <Typography variant="h4" color="black" textTransform="uppercase">
                                                {"No Products found"}
                                            </Typography>
                                        </Grid>
                                    )}
                                    <Grid container item xs={12} spacing={1} maxHeight={"fit-content !important"}
                                          width={"100%"}>
                                        {data?.codeCatalog?.data?.hits.map((item) => (
                                            <Grid item xs={12} md={12} lg={12} key={item.id}>
                                                <CodeCatalogListViewItem item={item}/>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            }
                        </Grid>
                        <Grid item xs={hierarchyMdSize} md={hierarchyMdSize}/>
                        <Grid item xs={contentMdSize}
                              md={contentMdSize} sx={{
                            display: "flex",
                            justifyContent: "center",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            zIndex: 2
                        }}>
                            {!!data?.codeCatalog?.data?.pagination && data?.codeCatalog?.data?.pagination.totalCount > CODE_CATALOG_LIMIT_PER_PAGE && (
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                    my: 2,
                                    height: "48px"
                                }}>
                                    <Pagination
                                        color="secondary"
                                        page={page}
                                        count={data?.codeCatalog?.data?.pagination.totalPages}
                                        onChange={(_, page) => {
                                            const currentParams = new URLSearchParams(searchParams);
                                            currentParams.set("page", page.toString());
                                            setSearchParams(currentParams);
                                        }}
                                        renderItem={(item) => <PaginationItem {...item} />}
                                    />
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </CodeCatalogWrapper>

        </LocalizationProvider>
    )
}
export default CodeCatalogView;